/**
 * Image compression utility for client-side image processing
 * Resizes and compresses images before upload to improve reliability
 */

const MAX_DIMENSION = 800;
const QUALITY = 0.75;
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

/**
 * Compress an image file or base64 string
 * @param {File|string} input - File object or base64 string
 * @param {Object} opts
 * @param {number} opts.maxDimension - Max width/height
 * @param {number} opts.quality - JPEG quality 0-1
 * @returns {Promise<{base64: string, blob: Blob, width: number, height: number}>}
 */
export async function compressImage(input, opts = {}) {
  const maxDim = opts.maxDimension || MAX_DIMENSION;
  const quality = opts.quality || QUALITY;

  const img = await loadImage(input);
  
  let { width, height } = img;
  
  // Scale down if needed
  if (width > maxDim || height > maxDim) {
    const ratio = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          base64: reader.result.split(',')[1],
          blob,
          width,
          height,
          dataUrl: reader.result,
        });
      };
      reader.readAsDataURL(blob);
    }, 'image/jpeg', quality);
  });
}

/**
 * Create an object URL for instant preview from a File
 */
export function createPreviewUrl(file) {
  return URL.createObjectURL(file);
}

/**
 * Validate image file before processing
 */
export function validateImageFile(file) {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
  if (!validTypes.includes(file.type)) {
    return { valid: false, error: 'Unsupported image format. Use JPEG, PNG, or WebP.' };
  }
  if (file.size > 20 * 1024 * 1024) {
    return { valid: false, error: 'Image too large. Max 20MB.' };
  }
  return { valid: true };
}

function loadImage(input) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    
    if (typeof input === 'string') {
      // base64 string - add data URL prefix if missing
      img.src = input.startsWith('data:') ? input : `data:image/jpeg;base64,${input}`;
    } else if (input instanceof File || input instanceof Blob) {
      img.src = URL.createObjectURL(input);
    } else {
      reject(new Error('Invalid input type'));
    }
  });
}
